import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Box, Button, Stack, Tooltip } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { useFormik } from 'formik';
import * as yup from 'yup';
import dayjs from 'dayjs';

import * as taxApi from '../../lib/api/taxbill';
import * as billCollectPlanApi from '../../lib/api/billCollectPlan';
import TaxbillList from '../../components/taxbillmng/TaxbillList';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import {
  listBondsDepts,
  setBondsDeptsFilters,
  resetBondsDeptsFilters,
} from '../../modules/taxbillModule/bonds-depts';
import { BondsDeptsListBox } from '../../components/bonds_depts/BondsDeptsListBox';
import BondsDeptsStatusList from '../../components/bonds_depts/BondsDeptsStatusList';
import BondDeptModal from '../../components/bonds_depts/BondDeptModal';
import BillPlanSummaryDialog from '../../components/bonds_depts/BillPlanSummaryDialog';
import TaxbillTotalSummary from '../../components/taxbillmng/TaxbillTotalSummary';
import { buildCsv, pickRowsToExport, downloadCsv } from '../../utils/csvExport';

const BondsDeptsListContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectRow] = useState(null);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const handleOpenModal = (row) => {
    setOpenModal(true);
    setSelectRow(row);
  };

  const handleClose = () => {
    setOpenModal(false);
    setSelectRow(null);
  };

  const { totalPrice, bonds_depts, defaultFilters, error, loading } =
    useSelector(({ taxbillModule, loading }) => ({
      totalPrice: taxbillModule.bondsDepts.totalPrice,
      bonds_depts: taxbillModule.bondsDepts.bonds_depts,
      defaultFilters: taxbillModule.bondsDepts.defaultFilters,
      error: taxbillModule.bondsDepts.error,
      loading: loading['taxbill/LIST_BONDS_DEPTS'],
    }));

  const [tableData, setTableData] = useState([]);
  // 다음 예정일 요약 + 펼침(2026-10-21 수금/지급 계획 N개, 설계문서 4장) - Community DataGrid는
  // 행 펼침(detail panel)이 Pro 전용이라 다이얼로그로 "펼쳐서 전체 계획 보기"를 대신한다.
  const [planSummaryTarget, setPlanSummaryTarget] = useState(null);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      invTyp: filters.searchInvTyp,
      searchTyp: filters.searchTyp,
      reqCompYn: filters.searchYn,
      procCompYn: filters.procCompYn,
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listBondsDepts(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (bonds_depts) {
      setTableData(bonds_depts);
      // 목록이 새로 바뀌면(필터 변경·재조회) 이전 선택은 더 이상 의미가 없다.
      setRowSelectionModel([]);
    }
  }, [bonds_depts]);

  const handleFilters = useCallback(
    (name, value) => {
      const newFilters = { ...defaultFilters, [name]: value };

      dispatch(setBondsDeptsFilters(newFilters));
    },
    [defaultFilters, dispatch],
  );

  const handleSave = async (data, msg) => {
    try {
      const response = await taxApi.onCollectBuyPay(data);
      if (response.data.info) {
        enqueueSnackbar(`${msg}되었습니다`, {
          variant: 'success',
        });
        dispatch(listBondsDepts(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      { field: 'id', hide: true, sortable: false, editable: false },
      { field: 'invUuid', hide: true, sortable: false, editable: false }, //세금계산서ID
      { field: 'roUuid', hide: true, sortable: false, editable: false }, //수주ID
      { field: 'billUuid', hide: true, sortable: false, editable: false }, //거래ID
      { field: 'prjUuid', hide: true, sortable: false, editable: false }, //프로젝트ID
      {
        field: 'invYn',
        hide: true,
        editable: false,
      },
      {
        field: 'invNo',
        headerName: '계산서번호',
        hide: true,
        editable: false,
      },
      {
        field: 'invTyp',
        headerName: '유형',
        hide: true,
        editable: false,
      },

      {
        field: 'reqCompDt',
        headerName: '발행일자',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'string',
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: 'procPlanDt',
        headerName: '지급(수금)예정일자',
        description: '다음 예정일 - 계획이 N개(선금/중도금/잔금)면 그중 가장 이른 미완료 계획',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 200,
        renderCell: (params) => (
          <Tooltip title="계획 펼쳐보기(선금/중도금/잔금)">
            <Button
              size="small"
              startIcon={<EventNoteIcon fontSize="small" />}
              onClick={() => setPlanSummaryTarget(params.row)}
            >
              {params.value || '-'}
            </Button>
          </Tooltip>
        ),
      },
      {
        field: 'procPlanYn',
        headerName: '지급(수금)예정완료여부',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 180,
      },
      {
        // 완료일자(BITO_0425 p43 / 0515 p54 "완료일자 추가"). DB 컬럼 PROC_COMP_DT는 이미 있었지만
        // 수금/지급 확정 모달이 이 값을 보내지 않아서 계속 비어 있었다(BondDeptModal.js에서 같이 고침).
        field: 'procCompDt',
        headerName: '완료일자',
        description: '수금(지급) 완료일자',
        align: 'center',
        headerAlign: 'center',
        type: 'string',
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: 'rsltCompDt',
        headerName: '실적일자',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'string',
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: 'action',
        headerName: '수금/지급',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Button
            size="small"
            variant="contained"
            color={params.row.invTyp === '매출' ? 'success' : 'secondary'}
            onClick={() => handleOpenModal(params.row)}
          >
            {params.row.invTyp === '매출' ? '수금' : '지급'}
          </Button>
        ),
      },
      {
        field: 'partnerNm',
        headerName: '고객명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      
      {
        field: 'invTxt',
        headerName: '발급내용',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'invAmt',
        headerName: '계산서 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },
      {
        field: 'invVat',
        headerName: 'VAT 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },
      {
        field: 'invTot',
        headerName: '합계 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },
      {
        field: 'osTran',
        headerName: '해외거래',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'reqPlanUsrNm',
        headerName: '발행요청자',
        description: '발행요청자',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
     
      {
        field: 'salesUsrNm',
        headerName: '영업 사원',
        description: '영업 사원',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
    ],
    [],
  );

  // 선택 내역 CSV 내보내기(BITO_0425 p34/0515 p45 "선택 checkbox 추가" + p43/p54 "Excel Download는
  // CSV 기능으로 대체" — 두 요구를 합치면 "체크박스로 선택한 행을 CSV로 내보낸다"). 선택이 없으면
  // 현재 필터로 걸러진 전체 목록을 내보낸다.
  //
  // 2026-10-21 2차 릴리스: procPlanDt 칸은 여전히 회차 단일값(레거시)이라, 계획이 선금/중도금/
  // 잔금으로 쪼개진 회차는 CSV만 보고는 "다음에 얼마가 남았는지" 알 수 없다 - 행마다 계획
  // 목록(getPlans)을 조회해 "다음계획일/다음계획잔액/계획내역" 3칸을 더해 내보낸다(설계문서 §8
  // "2차 릴리스 TODO" 3번, "계획 단위로 펼치는 옵션"을 칸 추가로 구현).
  const [exporting, setExporting] = useState(false);
  const handleExportCsv = useCallback(async () => {
    const exportColumns = columns.filter(
      (c) =>
        !['id', 'invUuid', 'roUuid', 'billUuid', 'prjUuid', 'invYn', 'action'].includes(
          c.field,
        ),
    );
    const rowsToExport = pickRowsToExport(tableData, rowSelectionModel);
    if (rowsToExport.length === 0) {
      enqueueSnackbar('내보낼 내역이 없습니다', { variant: 'warning' });
      return;
    }

    setExporting(true);
    try {
      const planLists = await Promise.all(
        rowsToExport.map((row) =>
          row.invUuid
            ? billCollectPlanApi
                .getPlans(row.invUuid)
                .then((res) => res?.data?.data || [])
                .catch(() => null) // 조회 실패한 행은 계획 칸을 비워 두고 나머지는 계속 내보낸다.
            : Promise.resolve(null),
        ),
      );

      const rowsWithPlan = rowsToExport.map((row, idx) => {
        const plans = planLists[idx];
        if (!Array.isArray(plans) || plans.length === 0) {
          return { ...row, planNextDt: '', planRemainAmt: '', planBreakdown: '' };
        }
        const unfinished = plans.filter((p) => Number(p.remainAmt) > 0);
        const next = unfinished[0];
        return {
          ...row,
          planNextDt: next ? `${next.planDt}${next.status === '지연' ? '(지연)' : ''}` : '완료',
          planRemainAmt: next ? next.remainAmt : '0',
          planBreakdown: plans
            .map(
              (p) =>
                `${p.planNm || '계획'}:${Number(p.planAmt).toLocaleString()}(${p.status})`,
            )
            .join(' | '),
        };
      });

      const csvColumns = [
        ...exportColumns,
        { field: 'planNextDt', headerName: '다음계획일' },
        { field: 'planRemainAmt', headerName: '다음계획잔액' },
        { field: 'planBreakdown', headerName: '계획내역(선금/중도금/잔금)' },
      ];
      const csv = buildCsv(csvColumns, rowsWithPlan);
      downloadCsv(csv, `채권채무_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    } catch (err) {
      enqueueSnackbar('계획 내역을 불러오지 못해 CSV 내보내기를 중단했습니다', {
        variant: 'error',
      });
    } finally {
      setExporting(false);
    }
  }, [columns, tableData, rowSelectionModel, enqueueSnackbar]);

  //검색 관련 사항
  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      const searchData = {
        searchInvTyp: defaultFilters.searchInvTyp,
        searchTyp: defaultFilters.searchTyp,
        searchYn: defaultFilters.searchYn,
        procCompYn: defaultFilters.procCompYn,
        searchKeyword: values.searchKeyword,
        searchWords: values.searchWords,
        searchStrtDt: values.searchStrtDt,
        searchEndDt: values.searchEndDt,
      };
      dispatch(setBondsDeptsFilters(searchData));
      setTimeout(() => {
        setSubmitting(false);
      }, 1000);
    },
  });

  return (
    <>
      <BreadcrumbsContainer
        title="채권&채무 관리/채권&채무 관리/채권&채무 리스트"
        icon="bonds_dept"
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCsv}
            disabled={exporting}
          >
            {exporting ? '내보내는 중...' : '선택 내역 CSV 내보내기'}
          </Button>
        }
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <BondsDeptsListBox formik={formik} onReset={() => dispatch(resetBondsDeptsFilters())} />
        <BondsDeptsStatusList
          filters={defaultFilters}
          onFilters={handleFilters}
        />
        <TaxbillTotalSummary totalPrice={totalPrice} />
        <TaxbillList
          columns={columns}
          rows={tableData}
          error={error}
          loading={loading}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(model) => setRowSelectionModel(model)}

          columnVisibilityModel={{
            id: false,
            invUuid: false,
            roUuid: false,
            billUuid: false,
            prjUuid: false,
            invYn: false,
            invTyp: false,
          }}

        />
      </Box>

      <BondDeptModal
        open={openModal}
        data={selectedRow}
        handleClose={handleClose}
        onSave={handleSave}
      />
      <BillPlanSummaryDialog
        open={Boolean(planSummaryTarget)}
        invUuid={planSummaryTarget?.invUuid}
        invNo={planSummaryTarget?.invNo}
        onClose={() => setPlanSummaryTarget(null)}
      />
    </>
  );
};

export default BondsDeptsListContainer;
